import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AnalysisNav } from './ui/analysis-nav';
import { RainbowButton } from "./ui/rainbow-button";
import { Card } from './ui/card';
import { StarsBackground } from './ui/stars-background';
import { motion } from "framer-motion";
import { PdfDownloadButton } from './ui/pdf-download-button';
import { fadeIn } from "../utils/motion";
import { FileText, TrendingUp, Users, Target, Lightbulb, BarChart3 } from 'lucide-react';
import { EnhancedMarkdown } from './ui/enhanced-markdown';
import { MarketResearchProgressCard } from './ui/market-research-progress-card';
import { FullscreenProgress } from './ui/fullscreen-progress';
import { trackFeatureUsage, trackEvent } from '../analytics/posthog';


const marketResearchSteps = [
  "Interviewing your competitors' chatbots for insider info 🤖",
  "Googling 'How to disrupt your industry' in incognito mode",
  "Calculating how many VCs will slide into your DMs 💌",
  "Checking if your market is 'ripe for disruption' (spoiler: it always is)",
  "Measuring the exact size of your Total Addressable Universe 🌌",
  "Finding out which Big Tech company will acquire you",
  "Analyzing if your startup name passes the domain availability test",
  "Determining your Peter Thiel competition score (0 to 1)",
  "Counting how many times Gartner mentioned your space this year",
  "Verifying you're not accidentally building Uber for X again",
  "Cross-referencing with Y Combinator's list of startup ideas",
  "Calculating your disruption coefficient using quantum algorithms",
  "Checking if Marc Andreessen tweeted about your market yet",
  "Measuring market FOMO levels on a scale of 1 to NFT",
  "Ensuring your TAM is bigger than crypto's market cap (on a good day)",
];

interface MarketResearchData {
  id: number;
  company_name: string;
  industry: string;
  pitch_deck_id: number | null;
  keywords: string[] | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  results: any;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  processed_results?: any; // Added field for LLM-processed results
}


// Helper function to safely parse JSON strings
const safeParseJson = (value: any): any => {
  if (typeof value !== 'string') {
    return value;
  }
  
  try {
    // Check if the string looks like JSON
    if (value.trim().startsWith('{') && value.trim().endsWith('}')) {
      return JSON.parse(value);
    }
    return value;
  } catch (e) {
    return value;
  }
};

// Helper function to convert objects to readable markdown format
const safeStringify = (data: any): string => {
  if (data === null || data === undefined) return '';
  
  // First try to parse if it's a JSON string
  const parsedData = safeParseJson(data);
  
  // Special case for executive_summary and similar fields that might contain a JSON string with the content
  if (typeof parsedData === 'object' && parsedData !== null) {
    // Check if this is a JSON object with a single key that matches the field name
    // For example: {"executive_summary": "# Executive Summary..."}
    const keys = Object.keys(parsedData);
    if (keys.length === 1 && typeof parsedData[keys[0]] === 'string') {
      // Return just the content string
      return parsedData[keys[0]];
    }
  }
  
  if (typeof parsedData === 'string') return parsedData;
  
  if (typeof parsedData === 'object') {
    try {
      // For arrays, format as a numbered markdown list
      if (Array.isArray(parsedData)) {
        if (parsedData.length === 0) return '';
        
        return parsedData.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            return `${index + 1}. ${safeStringify(item).replace(/\n/g, '\n   ')}`;
          }
          return `${index + 1}. ${item}`;
        }).join('\n');
      }
      
      // For objects, format as markdown sections with better formatting
      let result = '';
      for (const [key, value] of Object.entries(parsedData)) {
        // Skip null or undefined values
        if (value === null || value === undefined) continue;
        
        // Format the key nicely
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            // Format arrays with a header and numbered list
            if (value.length === 0) continue;
            
            result += `### ${formattedKey}\n\n`;
            value.forEach((item, index) => {
              result += `${index + 1}. ${typeof item === 'object' ? safeStringify(item) : item}\n`;
            });
            result += '\n';
          } else {
            // Format nested objects with a header
            const nestedContent = safeStringify(value);
            if (nestedContent.trim()) {
              result += `### ${formattedKey}\n\n${nestedContent}\n\n`;
            }
          }
        } else {
          // Format simple key-value pairs
          result += `**${formattedKey}**: ${value}\n\n`;
        }
      }
      return result;
    } catch (e) {
      console.error('Error formatting object as markdown:', e);
      return '*[Complex Object]*';
    }
  }
  
  return String(parsedData);
};

// Add this function to clean up duplicate links in markdown
const cleanMarkdown = (markdown: any) => {
  // Ensure markdown is a string
  if (!markdown) return '';
  
  // Convert objects to strings if needed
  let markdownStr = '';
  if (typeof markdown !== 'string') {
    console.warn('cleanMarkdown received non-string input:', markdown);
    markdownStr = safeStringify(markdown);
  } else {
    markdownStr = markdown;
  }
  
  try {
    // Replace patterns like [https://example.com](https://example.com) with just https://example.com
    let cleaned = markdownStr.replace(/\[(\bhttps?:\/\/[^\s\]]+)\]\(\1\)/g, '$1');
    
    // Remove patterns like [url](url) that appear after "Source: url"
    cleaned = cleaned.replace(/(Source:.*?)(\s*\[.*?\]\(.*?\))/gi, '$1');
    
    return cleaned;
  } catch (error) {
    console.error('Error in cleanMarkdown:', error);
    return markdownStr;
  }
};

// Component for rendering market research sections with better styling
const MarketResearchSection: React.FC<{
  title: string;
  content: string;
  icon?: React.ReactNode;
  delay?: number;
}> = ({ title, content, icon, delay = 0.3 }) => {
  if (!content || content.trim() === '') return null;
  
  return (
    <motion.div
      variants={fadeIn("up", "spring", delay, 0.75)}
      initial="hidden"
      animate="show"
      className="w-full"
    >
      <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
          <StarsBackground />
        </div>
        <div className="relative z-10">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
            {icon || <FileText className="mr-2 text-blue-500" size={26} />}
            {title}
          </h2>
          <div className="space-y-4 text-left">
            <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-800">
              <EnhancedMarkdown 
                content={cleanMarkdown(content)}
                className="text-gray-700"
              />
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

const MarketResearch: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>();
  const { api } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [researchList, setResearchList] = useState<MarketResearchData[]>([]);
  const [selectedResearch, setSelectedResearch] = useState<MarketResearchData | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');
  const [subsector, setSubsector] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [credits, setCredits] = useState<number>(0);
  const [creditsLoading, setCreditsLoading] = useState<boolean>(true);
  const [productInfo, setProductInfo] = useState<string>('');
  const [processingData, setProcessingData] = useState<boolean>(false);
  const processingResearchIds = useRef<Set<number>>(new Set());

  const fetchResearchList = useCallback(async () => {
    try {
      const response = await api.get(`/api/market-research?id=${id}`);
      
      setResearchList(response.data.research);
      
      // Update company, industry, subsector from pitch deck data
      if (response.data.pitch_deck) {
        setCompanyName(response.data.pitch_deck.company_name || '');
        setIndustry(response.data.pitch_deck.industry || '');
        setSubsector(response.data.pitch_deck.subsector || '');
      }
      
      // Extract product information from investment_analysis_data if available
      if (response.data.analysis_version && response.data.analysis_version.investment_analysis_data) {
        try {
          const analysisData = response.data.analysis_version.investment_analysis_data;
          
          // Extract product information from the correct fields in the JSON structure
          const productInfoSources = [
            // Vision and value proposition section
            analysisData.sections?.["vision and value prop"]?.vision_clarity,
            analysisData.sections?.["vision and value prop"]?.unique_value_prop,
            
            // Problem analysis section
            analysisData.problem_analysis?.problem_definition,
            analysisData.problem_analysis?.target_audience,
            
            // Solution and tech section
            analysisData.solution_and_tech?.problem_solution_fit,
            analysisData.solution_and_tech?.competitive_advantage,
            
            // Business model
            analysisData.business_model?.revenue_model,
            
            // Market opportunity
            analysisData.market_opportunity?.market_size,
            analysisData.market_opportunity?.market_trends
          ];
          
          // Combine all available product information
          const productInfo = productInfoSources
            .filter(source => source && typeof source === 'string')
            .join(". ");
          
          if (productInfo) {
            setProductInfo(productInfo);
          } else {
            console.log("No product info found in analysis data");
          }
        } catch (err) {
          console.error("Error parsing investment analysis data:", err);
        }
      }
      
      // If we have a selected research, update it
      if (selectedResearch) {
        const updated = response.data.research.find((r: MarketResearchData) => r.id === selectedResearch.id);
        if (updated) {
          setSelectedResearch(updated);
        }
      } else if (response.data.research.length > 0) {
        // Select the first one if none is selected
        setSelectedResearch(response.data.research[0]);
      }
    } catch (err) {
      console.error('Error fetching market research:', err);
      setError('Failed to load market research data');
    }
  }, [api, id]);

  // Fetch credit balance
  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await api.get('/api/credits/balance');
      setCredits(response.data.credits);
    } catch (err) {
      console.error('Error fetching credits:', err);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Function to process the raw JSON data through an LLM
  const processResearchData = useCallback(async (research: MarketResearchData) => {
    if (!research || !research.results || processingData) return;
    
    // Check if this research is already being processed
    if (processingResearchIds.current.has(research.id)) {
      console.log(`Research ${research.id} is already being processed, skipping...`);
      return;
    }
    
    try {
      setProcessingData(true);
      processingResearchIds.current.add(research.id);
      
      // Prepare the raw data to send to the backend
      const rawData = JSON.stringify(research.results);
      
      // Call the backend to process the data through an LLM
      const response = await api.post('/api/process-market-research', {
        research_id: research.id,
        raw_data: rawData
      });
    
      
      if (response.data && response.data.processed_results) {
        // Ensure all processed results are strings
        const processedResults = { ...response.data.processed_results };
        
        // Convert any non-string values to strings
        Object.keys(processedResults).forEach(key => {
          if (typeof processedResults[key] !== 'string') {
            processedResults[key] = safeStringify(processedResults[key]);
          }
        });
        
        // Handle nested objects in additional_sections if it exists
        if (processedResults.additional_sections && typeof processedResults.additional_sections === 'object') {
          const additionalSections = { ...processedResults.additional_sections };
          Object.keys(additionalSections).forEach(key => {
            if (typeof additionalSections[key] !== 'string') {
              additionalSections[key] = safeStringify(additionalSections[key]);
            }
          });
          processedResults.additional_sections = additionalSections;
        }
        
        // Update the selected research with processed results
        setSelectedResearch(prev => {
          if (prev && prev.id === research.id) {
            const updated = {
              ...prev,
              processed_results: processedResults
            };
            return updated;
          }
          return prev;
        });
        
        // Also update in the research list
        setResearchList(prev => {
          const updated = prev.map(item => 
            item.id === research.id 
              ? { ...item, processed_results: processedResults } 
              : item
          );
          return updated;
        });
        
        // Fetch the data again to verify it was saved to the database
        setTimeout(() => {
          fetchResearchList();
        }, 5000); // Increased delay to allow database to fully update
      }
    } catch (err) {
      console.error('Error processing research data through LLM:', err);
      setError('Failed to process research data');
    } finally {
      setProcessingData(false);
      processingResearchIds.current.delete(research.id);
    }
  }, [api, processingData, fetchResearchList]);

  useEffect(() => {
    fetchResearchList();
    fetchCredits();
    
    // Set up polling for updates if there are any in-progress research tasks
    const interval = setInterval(() => {
      const hasInProgress = researchList.some(r => r.status === 'pending' || r.status === 'processing');
      if (hasInProgress) {
        fetchResearchList();
      }
    }, 5000);
    
    setRefreshInterval(interval);
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [id]);
  
  // Monitor for research completion and auto-trigger post-processing
  useEffect(() => {
    if (selectedResearch && 
        selectedResearch.status === 'completed' && 
        selectedResearch.results && 
        !selectedResearch.processed_results &&
        !processingData) {
      console.log('Research completed, auto-triggering post-processing...');
      processResearchData(selectedResearch);
    }
  }, [selectedResearch, processingData, processResearchData]);

  const handleCreateResearch = async () => {
    if (!companyName || !industry) {
      setError('Company name and industry are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // First check if user has enough credits
      const accessCheck = await api.get('/api/credits/check-access/market-research');
      
      if (!accessCheck.data.can_access) {
        setError(accessCheck.data.message);
        return;
      }

      // If they have credits, proceed with research and deduct credits
      const useCredits = await api.post('/api/credits/use-feature/market-research');
      
      if (!useCredits.data.success) {
        setError(useCredits.data.message);
        return;
      }

      // Track market research creation
      trackFeatureUsage('market_research', 'create_started', {
        company_name: companyName,
        industry: industry,
        has_product_info: productInfo.length > 0,
        analysis_id: id
      });
      
      // Now proceed with the research - include product info
      const response = await api.post('/api/market-research', {
        company_name: companyName,
        industry,
        analysis_id: id ? parseInt(id) : null,
        product_info: productInfo // Pass the product info to the backend
      });
      
      // Track successful creation
      trackFeatureUsage('market_research', 'create_success', {
        company_name: companyName,
        industry: industry
      });
      
      // Clear form
      setCompanyName('');
      setIndustry('');
      // Remove this line: setKeywords('');
      
      // Refresh the list and credit balance
      fetchResearchList();
      fetchCredits(); // Update credit balance after using credits
    } catch (err: any) {
      console.error('Error creating market research:', err);
      setError(err.response?.data?.error || 'Failed to create market research');
      
      // Track creation failure
      trackFeatureUsage('market_research', 'create_failed', {
        error_message: err.response?.data?.error || err.message,
        company_name: companyName,
        industry: industry
      });
    } finally {
      setLoading(false);
    }
  };


  // Handle PDF generation errors
  const handlePdfError = (error: Error) => {
    console.error('Error generating PDF:', error);
    setError('Failed to generate PDF. Please try again.');
  };

  const renderResearchResults = () => {
    if (!selectedResearch) {
      return null;
    }
    
    if (selectedResearch.status === 'pending' || selectedResearch.status === 'processing') {
      return (
        <div className="relative min-h-[800px]">
          <FullscreenProgress
            status={selectedResearch.status}
            title={selectedResearch.status === 'pending' ? 'Preparing Market Intelligence' : 'Conducting Market Research'}
            subtitle="Our AI is diving deep into the market data ocean 🌊"
            steps={marketResearchSteps}
            stepDuration={3000}
            progressIncrement={{ min: 0.5, max: 2.5 }}
            progressInterval={1000}
            completionThreshold={98}
            onComplete={() => {
              console.log('Research completed, starting post-processing...');
            }}
          />
        </div>
      );
    }
    
    if (selectedResearch.status === 'failed') {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <Card className="max-w-2xl w-full p-8 bg-white/95 backdrop-blur-sm shadow-xl border-red-200">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Research Failed</h2>
              <p className="text-gray-600 mb-6">{selectedResearch.error_message || 'An error occurred during market research analysis.'}</p>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          </Card>
        </div>
      );
    }
    
    if (!selectedResearch.results) {
      return (
        <div className="relative min-h-[600px]">
          <FullscreenProgress
            status="processing"
            title="Processing Market Research"
            subtitle="Our AI is analyzing the collected data 📊"
            steps={marketResearchSteps}
            stepDuration={3000}
            progressIncrement={{ min: 0.5, max: 2.5 }}
            progressInterval={1000}
            completionThreshold={98}
            onComplete={() => {
              console.log('Research completed, starting post-processing...');
            }}
          />
        </div>
      );
    }
    
    if (processingData) {
      return (
        <Box p={3}>
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <CircularProgress size={24} />
              <Typography variant="h6" className="text-blue-900">
                Enhancing your market research report...
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Our AI is formatting the data for better readability and insights. This usually takes 10-30 seconds.
            </Typography>
            <Box mt={3}>
              <Typography variant="caption" color="text.secondary">
                💡 Pro tip: We're making tables prettier, organizing insights, and adding visual elements!
              </Typography>
            </Box>
          </Card>
        </Box>
      );
    }
    
    // Render the research results
    return (
      <Box p={3} ref={contentRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5">Market Analysis for {selectedResearch.company_name}</Typography>
          <Chip
            label={selectedResearch.status}
            color={selectedResearch.status === 'completed' ? 'success' : 'default'}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {new Date(selectedResearch.created_at).toLocaleDateString()}
          </Typography>
          {!processingData && !selectedResearch.processed_results && (
            <Button 
              variant="outlined" 
              color="primary" 
              size="small"
              onClick={() => processResearchData(selectedResearch)}
            >
              Process with AI
            </Button>
          )}
          </Box>
          <Box>
            <PdfDownloadButton
              contentRef={contentRef}
              fileName={`${selectedResearch.company_name}_Market_Research`}
              documentTitle={`Market Research - ${selectedResearch.company_name}`}
              documentSubject={`Market Analysis for ${selectedResearch.company_name}`}
              loading={loading}
              setLoading={setLoading}
              onError={handlePdfError}
              position="right"
            />
          </Box>
        </Box>
        
        {/* Display processed results if available */}
        {selectedResearch.processed_results && (
          <Box mb={4}>
            {/* Display executive summary */}
            <MarketResearchSection
              title="Executive Summary"
              content={safeStringify(selectedResearch.processed_results?.executive_summary)}
              icon={<BarChart3 className="mr-2 text-blue-500" size={26} />}
              delay={0.3}
            />
            
            {/* Display key insights section */}
            <MarketResearchSection
              title="Key Market Insights"
              content={safeStringify(selectedResearch.processed_results?.key_insights)}
              icon={<Lightbulb className="mr-2 text-yellow-500" size={26} />}
              delay={0.4}
            />
            
            {/* Display market trends section */}
            <MarketResearchSection
              title="Market Trends"
              content={safeStringify(selectedResearch.processed_results?.market_trends)}
              icon={<TrendingUp className="mr-2 text-green-500" size={26} />}
              delay={0.5}
            />
            
            {/* Display competitive landscape section */}
            <MarketResearchSection
              title="Competitive Landscape"
              content={safeStringify(selectedResearch.processed_results?.competitive_landscape)}
              icon={<Users className="mr-2 text-purple-500" size={26} />}
              delay={0.6}
            />
            
            {/* Display recommendations section */}
            <MarketResearchSection
              title="Strategic Recommendations"
              content={safeStringify(selectedResearch.processed_results?.recommendations)}
              icon={<Target className="mr-2 text-red-500" size={26} />}
              delay={0.7}
            />
            
            {/* Display any additional sections */}
            {selectedResearch.processed_results.additional_sections && 
             typeof selectedResearch.processed_results.additional_sections === 'object' && 
             !Array.isArray(selectedResearch.processed_results.additional_sections) &&
             Object.entries(selectedResearch.processed_results.additional_sections).map(([key, value]) => {
               // Debug: Log the additional section value to identify issues
               // Skip if the key is numeric (likely from string iteration) or value is not a string
               if (/^\d+$/.test(key) || typeof value !== 'string' || value.length < 2) {
                 return null;
               }
               return (
                <motion.div
                  key={key}
                  variants={fadeIn("up", "spring", 0.8, 0.75)}
                  initial="hidden"
                  animate="show"
                  className="w-full"
                >
                  <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                    <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                      <StarsBackground />
                    </div>
                    <div className="relative z-10">
                      <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                        <FileText className="mr-2 text-blue-500" size={26} />
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h2>
                      <div className="space-y-4 text-left">
                        <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-800">
                          <EnhancedMarkdown 
                            content={cleanMarkdown(value)}
                            className="text-gray-700"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
             })}
          </Box>
        )}
        
        {/* Display the summary if we don't have processed results */}
        {!selectedResearch.processed_results && selectedResearch.results && selectedResearch.results.summary && (
          <Box mb={4}>
            <motion.div
              variants={fadeIn("up", "spring", 0.3, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                    <FileText className="mr-2 text-blue-500" size={26} />
                    Executive Summary
                  </h2>
                  <div className="space-y-4 text-left">
                    <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-800">
                      <EnhancedMarkdown 
                        content={cleanMarkdown(selectedResearch.results.summary)}
                        className="text-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </Box>
        )}
        
        {/* Add Full Report Section */}
        {/* Only show the Sources section from the raw data if it exists and we don't have processed results */}
        {!selectedResearch.processed_results && selectedResearch.results.sources && (
          <Box mb={4}>
            <motion.div
              variants={fadeIn("up", "spring", 0.5, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2">
                    <FileText className="mr-2 text-blue-500" size={26} />
                    Sources
                  </h2>
                  <div className="space-y-4 text-left">
                    <ol className="list-decimal pl-5">
                      {Array.isArray(selectedResearch.results.sources) 
                        ? selectedResearch.results.sources.map((source: string, i: number) => (
                            <li key={i} className="mb-2 text-gray-700">
                              <EnhancedMarkdown 
                                content={cleanMarkdown(source)}
                                className="inline"
                              />
                            </li>
                          ))
                        : typeof selectedResearch.results.sources === 'string' 
                          ? selectedResearch.results.sources.split('\n')
                              .filter((line: string) => line.trim())
                              .map((line: string, i: number) => (
                                <li key={i} className="mb-2 text-gray-700">
                                  <EnhancedMarkdown 
                                    content={cleanMarkdown(line)}
                                    className="inline"
                                  />
                                </li>
                              ))
                          : null
                      }
                    </ol>
                  </div>
                </div>
              </Card>
            </motion.div>
          </Box>
        )}
        
        {/* We're not rendering additional raw sections anymore - only showing processed data */}
      </Box>
    );
  };

  return (
    <>
      {id && <AnalysisNav id={id} />}
      
      <Box p={3}>
        
        {/* Only show the Generate Market Research form if no research data exists */}
        {researchList.length === 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6">Market Research</Typography>
            <Box mb={3}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Company: <Typography component="span" color="text.primary" fontWeight="medium">{companyName}</Typography>
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Industry: <Typography component="span" color="text.primary" fontWeight="medium">{industry}</Typography>
                {subsector && (
                  <Typography component="span" color="text.primary" fontWeight="medium">
                    {' '} | {subsector}
                  </Typography>
                )}
              </Typography>
            </Box>
            
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            
            <Box>
              <Box textAlign="center" mb={2}>
                {credits < 5 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    <a href="/credits" className="text-blue-500 hover:text-blue-700">
                      Get more credits
                    </a>
                  </Typography>
                )}
              </Box>
              <RainbowButton 
                onClick={handleCreateResearch}
                disabled={loading || creditsLoading || credits < 5}
                className={`mt-2 ${credits < 5 ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
              >
                {loading ? <CircularProgress size={24} /> : 
                 creditsLoading ? <CircularProgress size={24} /> :
                 credits < 5 ? (
                  <div className="flex items-center justify-center">
                    <span className="text-red-500">Need More Credits</span>
                  </div>
                 ) : 'Generate Market Research (5 Credits)'}
              </RainbowButton>
            </Box>
          </Paper>
        )}
          
        {renderResearchResults()}
      </Box>
    </>
  );
};

export default MarketResearch; 